import { BookmarkApi } from './BookmarkApi';
import { Log } from '@lightningjs/sdk';
import { BaseCachedApi } from '../BaseCachedApi';
import { IBookmarkElementV1, IBookmarkUpdateV1 } from '../models/AndromeTV.types';
import { BookmarkFilter, BookmarkID } from './BookmarkApi.types';
import { Backend } from '../Backend';
import { AssetType } from '../models/Shared.types';
import { GlobalSettingsKeys } from '../models/GlobalSettingsKeys';
import { SettingsKeys } from '../models/TranslationKeys';

type bookmarkCallback = (bm: IBookmarkUpdateV1) => void;

export class CachedBookmarkApi extends BaseCachedApi {
  private _observers: Map<string, bookmarkCallback[]>;
  protected override _cache: Map<string, IBookmarkElementV1> | undefined;

  constructor(private backend: Backend) {
    super();
    this._observers = new Map();
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this.updateExpirations(
      this.backend.globalSettings.numberValue(SettingsKeys.bookmarkCacheExpiration) ?? 600000,
      this.backend.globalSettings.numberValue(SettingsKeys.bookmarkCacheErrorExpiration) ?? 300000
    );
  }

  registerObserver(bookmark: IBookmarkElementV1, callback: bookmarkCallback) {
    const observers = this._observers.get(this.bookmarkKey(bookmark)) || [];
    observers.push(callback);
    this._observers.set(this.bookmarkKey(bookmark), observers);
  }

  removeObserver(bookmark: IBookmarkElementV1, callback: bookmarkCallback) {
    let observers = this._observers.get(this.bookmarkKey(bookmark)) || [];
    observers = observers.filter(o => o !== callback);

    if (observers.length > 0) {
      this._observers.set(this.bookmarkKey(bookmark), observers);
    } else {
      this._observers.delete(this.bookmarkKey(bookmark));
    }
  }

  /**
   * Retrieve the bookmark with the specified type and id
   */
  bookmark(backend: Backend, bookmarkId: BookmarkID, adultAllowed = false): Promise<IBookmarkElementV1 | undefined> {
    const promise = this.checkCache(backend);

    return promise.then(() => {
      const bookmark = this._cache?.get(this.bookmarkKey(bookmarkId));
      if (bookmark && (!bookmark.adult || adultAllowed)) {
        return bookmark;
      }
      return undefined;
    });
  }

  /**
   * Retrieve the bookmark with the specified type and id
   * Use the short cache timeout for a specific bookmark see NEBINT-12143
   */
  bookmarkShortCache(
    backend: Backend,
    bookmarkId: BookmarkID,
    adultAllowed = false
  ): Promise<IBookmarkElementV1 | undefined> {
    const shortTimeout = backend.globalSettings.numberValue(GlobalSettingsKeys.recentBookmarkCacheWindowMs) || 120000;
    const shortCacheTime = this.lastUpdate + shortTimeout;

    if (new Date().getTime() < shortCacheTime) {
      // use the recently cached bookmark
      return this.bookmark(backend, bookmarkId, adultAllowed);
    } else {
      // refresh only the one bookmark
      return BookmarkApi.bookmark(backend, bookmarkId)
        .then(bookmark => {
          this._writeToCache(bookmarkId, bookmark);
          return bookmark;
        })
        .catch(e => {
          Log.error('[error fetching single bookmark] ', e);
          return undefined;
        });
    }
  }

  seriesBookmarks(
    backend: Backend,
    seriesId: string,
    assetType: AssetType,
    adultAllowed = false
  ): Promise<IBookmarkElementV1[]> {
    const promise = this.checkCache(backend);

    return promise.then(() => {
      let bookmarks = this._cache ? [...this._cache.values()] : [];
      bookmarks = bookmarks.sort((a, b) => {
        if (parseInt(a.lastUpdate, 10) < parseInt(b.lastUpdate, 10)) {
          return 1;
        } else if (parseInt(a.lastUpdate, 10) > parseInt(b.lastUpdate, 10)) {
          return -1;
        } else {
          return 0;
        }
      });
      return bookmarks.filter(
        bookmark => bookmark.type === assetType && bookmark.series === seriesId && (!bookmark.adult || adultAllowed)
      );
    });
  }

  /**
   * Create or update a bookmark
   * @param {Backend} backend
   * @param {BookmarkID} bookmarkId
   * @param {IBookmarkUpdateV1} value
   * @returns {Promise<Response>}
   */
  update(backend: Backend, bookmarkId: BookmarkID, value: IBookmarkUpdateV1): Promise<unknown> {
    if (!backend.isAuthorized()) return Promise.resolve();

    const promise = this.checkCache(backend);

    promise.then(() => {
      this._writeToCache(bookmarkId, value);
    });

    return BookmarkApi.update(backend, bookmarkId, value).then(response => {
      const observers = this._observers.get(this.bookmarkKey(bookmarkId));
      if (observers) {
        observers.forEach(o => o(value));
      }
      return response;
    });
  }

  /**
   * Remove a bookmark
   */
  delete(backend: Backend, bookmarkId: BookmarkID): Promise<unknown> {
    const promise = this.checkCache(backend);

    promise.then(() => {
      this._cache?.delete(this.bookmarkKey(bookmarkId));
    });

    return BookmarkApi.delete(backend, bookmarkId);
  }

  /**
   * Retrieve a list of bookmarks
   */
  list(backend: Backend, filter: BookmarkFilter): Promise<IBookmarkElementV1[]> {
    const promise = this.checkCache(backend);

    return promise.then(() => {
      if (this._cache)
        return Array.from(this._cache.values()).filter(bookmark => this.bookmarkPassesFilter(bookmark, filter));
      return [];
    });
  }

  override fillCache(backend: Backend): Promise<unknown> {
    this.updateGlobalSettings();
    // When Unauthenticated, do not perform this request, just return empty array.
    // @url https://androme.atlassian.net/browse/NEBINT-9750
    if (!backend.isAuthorized()) {
      this.clearCache();
      return Promise.resolve([]);
    }
    Log.info('[CachedBookmarkApi]', 'refreshing list, bypass:', this._bypassCache);
    return BookmarkApi.list(backend, {
      profileId: backend.deviceProfile?.id,
      catalogId: backend.deviceProfile?.profile.catalog,
    }).then(result => {
      this.clearCache();
      if (!this._cache) this._cache = new Map<string, IBookmarkElementV1>();
      result.forEach(bookmark => {
        this._cache?.set(this.bookmarkKey(bookmark), bookmark);
      });
      return this._cache;
    });
  }

  bookmarkPassesFilter(bookmark: IBookmarkElementV1, filter: BookmarkFilter) {
    return (
      (!filter.series || bookmark.series === filter.series) &&
      (!filter.type || bookmark.type === filter.type) &&
      (!filter.adult || bookmark.adult === filter.adult)
    );
  }

  bookmarkKey(bookmark: IBookmarkElementV1 | BookmarkID) {
    return bookmark.id + bookmark.type;
  }

  _writeToCache(bookmarkId: IBookmarkElementV1 | BookmarkID, value: IBookmarkUpdateV1) {
    const oldVal = this._cache?.get(this.bookmarkKey(bookmarkId));
    const newVal = {
      ...oldVal,
      ...value,
    } as IBookmarkElementV1;
    this._cache?.set(this.bookmarkKey(bookmarkId), newVal);
  }
}
